import {defineStore} from "pinia";
import {useScreen} from "@/core/store/screen";
import {Maybe} from "@/core/types";
import {useConfig} from "@/core/store/config";

const STORE_NAME = 'imager'

export type ImageSize =
    (keyof typeof ImageSizes)
    | 'w-1'
    | 'w-1-2'
    | 'w-1-3'
    | 'w-1-4'
    | number
    | string
    | [number, number, boolean?]

export const ImageSizes = {
    "m-1": {"op": "resize", "args": [600]},
    "m-1-2": {"op": "resize", "args": [300]},
    "m-1-3": {"op": "resize", "args": [200]},
    "m-1-4": {"op": "resize", "args": [150]},

    "t-1": {"op": "resize", "args": [1000]},
    "t-1-2": {"op": "resize", "args": [500]},
    "t-1-3": {"op": "resize", "args": [333]},
    "t-1-4": {"op": "resize", "args": [250]},

    "r70": {"op": "resize", "args": [70]},
    "r100": {"op": "resize", "args": [100]},
    "r200": {"op": "resize", "args": [200]},
    "r300": {"op": "resize", "args": [300]},
    "r400": {"op": "resize", "args": [400]},
    "r500": {"op": "resize", "args": [500]},
    "r600": {"op": "resize", "args": [600]},
    "r800": {"op": "resize", "args": [800]},
    "r1000": {"op": "resize", "args": [1000]},

    "m0.7": {"op": "crop", "args": [510, 728]},
    "m1": {"op": "crop", "args": [500, 500]},
    "m1.25": {"op": "crop", "args": [500, 400]},
    "m1.33": {"op": "crop", "args": [500, 375]},
    "m1.4": {"op": "crop", "args": [500, 357]},
    "m1.5": {"op": "crop", "args": [500, 333]},
    "m1.6": {"op": "crop", "args": [500, 312]},
    "m1.78": {"op": "crop", "args": [500, 281]},
    "m1.9": {"op": "crop", "args": [500, 263]},
    "m2": {"op": "crop", "args": [500, 250]},
    "m2.2": {"op": "crop", "args": [500, 227]},
    "m2.4": {"op": "crop", "args": [500, 208]},
    "m2.6": {"op": "crop", "args": [500, 192]},
    "m2.75": {"op": "crop", "args": [500, 181]},
    "m3": {"op": "crop", "args": [500, 166]},
    "m3.2": {"op": "crop", "args": [500, 156]},
    "m3.8": {"op": "crop", "args": [500, 131]},

    "t0.7": {"op": "crop", "args": [900, 1285]},
    "t1": {"op": "crop", "args": [900, 900]},
    "t1.25": {"op": "crop", "args": [900, 720]},
    "t1.33": {"op": "crop", "args": [900, 676]},
    "t1.4": {"op": "crop", "args": [900, 642]},
    "t1.5": {"op": "crop", "args": [900, 600]},
    "t1.6": {"op": "crop", "args": [900, 562]},
    "t1.78": {"op": "crop", "args": [900, 505]},
    "t1.9": {"op": "crop", "args": [900, 473]},
    "t2": {"op": "crop", "args": [900, 450]},
    "t2.2": {"op": "crop", "args": [900, 409]},
    "t2.4": {"op": "crop", "args": [900, 375]},
    "t2.6": {"op": "crop", "args": [900, 346]},
    "t2.75": {"op": "crop", "args": [900, 327]},
    "t3": {"op": "crop", "args": [900, 300]},

    "d1": {"op": "crop", "args": [1300, 1300]},
    "d1.25": {"op": "crop", "args": [1300, 1040]},
    "d1.33": {"op": "crop", "args": [1300, 977]},
    "d1.4": {"op": "crop", "args": [1300, 928]},
    "d1.5": {"op": "crop", "args": [1300, 866]},
    "d1.6": {"op": "crop", "args": [1300, 812]},
    "d1.78": {"op": "crop", "args": [1300, 730]},
    "d1.9": {"op": "crop", "args": [1300, 684]},
    "d2": {"op": "crop", "args": [1300, 650]},
    "d2.2": {"op": "crop", "args": [1300, 590]},
    "d2.4": {"op": "crop", "args": [1300, 541]},
    "d2.6": {"op": "crop", "args": [1300, 500]},
    "d2.75": {"op": "crop", "args": [1300, 472]},
    "d3": {"op": "crop", "args": [1300, 433]}
}

export const useImager = defineStore(STORE_NAME, () => {

    const {screenWidth, screenHeight} = useScreen()

    const {config} = useConfig()

    const resolveCache: Record<string, string> = {}

    /**
     * Получает ширину из параметра style.
     * style может быть числом, массивом [width, height] или строкой (кроме 'full').
     * Возвращает число или null.
     */
    function getWidthFromStyle(style: ImageSize): number | null {
        if (typeof style === 'number') {
            return style;
        }
        if (Array.isArray(style) && style.length > 0) {
            const w = Number(style[0]);
            return Number.isFinite(w) ? w : null;
        }
        if (typeof style === 'string' && style !== 'full') {
            const num = parseInt(style, 10);
            if (!isNaN(num)) return num;
        }
        return null;
    }

    const getStyleParams = (style?: ImageSize): any => {

        if (!style)
            return []

        let width, height, crop

        if (typeof style === 'string') {

            let matches = style.match(/([wh])\-(\d(\-\d)?)/)

            if (matches) {
                const sizeSide = matches[1]
                const sizeValue = matches[2]
                style = sizeSide === 'w' ?
                    [screenWidth > 1000 ? 't' : 'm', sizeValue].join('-')
                    :
                    [screenWidth > 1000 ? 't' : 'm', sizeValue].join('-')
            }

            let size = ImageSizes[style as keyof typeof ImageSizes]
            if (size) {
                [width, height] = size.args
                crop = size.op === 'crop'
            }
        } else if (typeof style === 'number') {
            width = style
            crop = false
        } else if (Array.isArray(style)) {
            [width, height, crop] = style
        } else {
            return []
        }

        const params: any = {}

        params.f = 'auto'
        params.c = crop ? 'thumb' : 'limit'

        if (width)
            params.w = width

        if (height)
            params.h = height

        return params
    }

    const imageResolve = (url: Maybe<string>, style?: ImageSize, isDev = false): string => {


        // Значение по умолчанию, как в исходной функции
        style = style || 2000;

        const cacheId = url + '-' + (style ? (Array.isArray(style) ? style.join('-') : style) : 'full') + (isDev ? '-dev' : '');

        if (resolveCache[cacheId]) {
            //return resolveCache[cacheId] || '';
        }

        if (!url) {
            return '';
        }

        // Dev‑режим и исключения – без изменений
        if (isDev) {
            return config.app.IMAGE_DEV_BASE_URL + url;
        }

        if (config.app.IMAGE_DEV_URLS && config.app.IMAGE_DEV_URLS.length) {
            for (const localUrl of config.app.IMAGE_DEV_URLS) {
                if (url.match(localUrl)) {
                    return config.app.IMAGE_DEV_BASE_URL + url;
                }
            }
        }

        // Если стиль явно 'full' или не удалось извлечь ширину – отдаём оригинал
        if (style === 'full' || !style) {
            const originalUrl = url.charAt(0) === '/' ? config.app.IMAGE_BASE_URL + url : url;
            resolveCache[cacheId] = originalUrl;
            return originalUrl;
        }


        const params = getStyleParams(style);

        const width = params?.w

        if (!width) {
            // fallback на оригинал, если ширину не определили
            const originalUrl = url.charAt(0) === '/' ? config.app.IMAGE_BASE_URL + url : url;
            resolveCache[cacheId] = originalUrl;
            return originalUrl;
        }

        // Добавляем -rw<ширина> перед расширением, сохраняем query-параметры
        const modifiedUrl = url.replace(/\.(jpg|jpeg|png)($|\?.*)/i, `-rw${width}.$1$2`);
        const fullUrl = modifiedUrl.charAt(0) === '/' ? config.app.IMAGE_BASE_URL + modifiedUrl : modifiedUrl;

        resolveCache[cacheId] = fullUrl;
        return fullUrl;
    }


    return {
        imageResolve
    }
})

